import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
}));

const FeaturePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const EUWaitlistPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Submit to waitlist
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}api/eu-waitlist/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name,
          country,
          source: 'eu_geo_block',
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        // Track successful signup
        if (window.gtag) {
          window.gtag('event', 'eu_waitlist_signup', {
            'country': country,
            'source': 'geo_block'
          });
        }
      } else {
        throw new Error('Failed to join waitlist');
      }
    } catch (err) {
      console.error('Waitlist signup error:', err);
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <StyledPaper elevation={3}>
          <RocketLaunchIcon sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom>
            You're on the list! 🎉
          </Typography>
          <Typography variant="h6" sx={{ mb: 3 }}>
            We'll notify you as soon as SoloSocial launches in Europe
          </Typography>
          <Typography variant="body1">
            Thank you for your patience while we ensure full GDPR compliance for our European users.
          </Typography>
        </StyledPaper>

        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            What happens next?
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" sx={{ mt: 2 }}>
            <Chip 
              icon={<SecurityIcon />} 
              label="GDPR Compliance Review" 
              color="primary" 
              variant="outlined" 
            />
            <Chip 
              icon={<NotificationsIcon />} 
              label="Email Notification" 
              color="secondary" 
              variant="outlined" 
            />
            <Chip 
              icon={<RocketLaunchIcon />} 
              label="EU Launch" 
              color="success" 
              variant="outlined" 
            />
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <StyledPaper elevation={3}>
        <LocationOnIcon sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom>
          Coming Soon to Europe! 🇪🇺
        </Typography>
        <Typography variant="h6" sx={{ mb: 3 }}>
          SoloSocial is preparing to launch in Europe with full GDPR compliance
        </Typography>
        <Typography variant="body1">
          We're working hard to ensure our platform meets all European data protection requirements.
          Join our waitlist to be notified as soon as we're ready!
        </Typography>
      </StyledPaper>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ mb: 3 }}>
          Why the wait?
        </Typography>
        
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={3}
          sx={{ mb: 4 }}
        >
          <FeaturePaper elevation={2}>
            <SecurityIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              GDPR Compliance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We're implementing comprehensive data protection measures to ensure your privacy rights are fully protected.
            </Typography>
          </FeaturePaper>

          <FeaturePaper elevation={2}>
            <LocationOnIcon color="secondary" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Local Data Centers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Setting up European infrastructure to keep your data within EU borders as required by law.
            </Typography>
          </FeaturePaper>

          <FeaturePaper elevation={2}>
            <NotificationsIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Legal Review
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Working with legal experts to ensure full compliance with European data protection regulations.
            </Typography>
          </FeaturePaper>
        </Stack>
      </Box>


      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom align="center">
          Join the EU Waitlist
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
          Be the first to know when we launch in Europe
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
            />
            
            <TextField
              fullWidth
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              helperText="We'll only use this to notify you about our EU launch"
            />
            
            <TextField
              fullWidth
              label="Country (Optional)"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={loading}
              helperText="Helps us prioritize launch regions"
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <NotificationsIcon />}
              sx={{ py: 1.5 }}
            >
              {loading ? 'Joining Waitlist...' : 'Join EU Waitlist'}
            </Button>
          </Stack>
        </Box>

        <Typography variant="caption" display="block" sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
          By joining our waitlist, you agree to receive email notifications about our EU launch.
          You can unsubscribe at any time.
        </Typography>
      </Paper>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Questions? Contact us at{' '}
          <a href="mailto:eu-support@solosocial.com" style={{ color: 'inherit' }}>
            eu-support@solosocial.com
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default EUWaitlistPage;